// ─── the tool bridge ─────────────────────────────────────────────────────────
// Tools cross the socket in both directions, and the session is authoritative in
// both:
//
//   session → client   `tool.call` with `runAt: 'client'` — something only the
//                      browser can do. We run it and MUST answer `tool.result`
//                      with the same callId, or the session's dispatch times out.
//   client → session   a client-placed provider's model wants a tool the session
//                      owns (builtin `deep_think`, or a caller webhook). We send
//                      `tool.call` up and wait for the matching `tool.result`.
//
// Both are the same table of pending promises keyed by callId, which is why they
// live in one small file instead of two.

import type { ClientEvent, ServerEvent } from '@shared/protocol.ts'
import type { ToolOutcome } from './types.ts'

export type ToolHandler = (args: unknown) => unknown | Promise<unknown>

export interface ToolBridgeOptions {
  emit(ev: ClientEvent): void
  /** Tools this app implements, by name. Only names the session declared are ever called. */
  handlers: Record<string, ToolHandler>
  /** Cap on a session round-trip, below the session's own tool timeout. */
  timeoutMs?: number
  log?(line: string): void
}

const DEFAULT_TIMEOUT_MS = 12_000

export class ToolBridge {
  private pending = new Map<string, (r: ToolOutcome) => void>()
  private seq = 0

  constructor(private readonly o: ToolBridgeOptions) {}

  /** Names the app can run. `session.ready.clientTools` decides which ones matter. */
  get names(): string[] {
    return Object.keys(this.o.handlers)
  }

  /** Feed every server event here; returns true if it was a tool frame we consumed. */
  accept(ev: ServerEvent): boolean {
    if (ev.t === 'tool.call' && ev.runAt === 'client') {
      void this.runForSession(ev.callId, ev.name, ev.args)
      return true
    }
    if (ev.t === 'tool.result') {
      const settle = this.pending.get(ev.callId)
      if (!settle) return false // the session's own dispatch, reported for the UI
      this.pending.delete(ev.callId)
      settle({ ok: ev.ok, result: ev.result, error: ev.error })
      return true
    }
    return false
  }

  /**
   * Run a tool for a client-placed model. Local if we implement it and the session
   * routed it to us, otherwise the session dispatches it and answers.
   */
  async run(name: string, args: unknown, clientTools: readonly string[]): Promise<ToolOutcome> {
    if (clientTools.includes(name) && this.o.handlers[name]) return this.local(name, args, true)
    return this.askSession(name, args)
  }

  private async runForSession(callId: string, name: string, args: unknown): Promise<void> {
    // Not logged: the session broadcasts `tool.call` and `tool.result` for this one,
    // and reporting it here as well would print the same round trip twice.
    const outcome = await this.local(name, args)
    this.o.emit({ t: 'tool.result', callId, name, ...outcome })
  }

  private async local(name: string, args: unknown, announce = false): Promise<ToolOutcome> {
    const handler = this.o.handlers[name]
    if (!handler) return { ok: false, error: `this client has no tool named "${name}"` }
    try {
      const result = await handler(args)
      // Only when nothing else will say it: a tool a client-placed model ran here
      // never crosses the socket, so this is its only trace.
      if (announce) this.o.log?.(`${name} ran here → ${JSON.stringify(result ?? null).slice(0, 120)}`)
      return { ok: true, result: result ?? null }
    } catch (err) {
      return { ok: false, error: err instanceof Error ? err.message : String(err) }
    }
  }

  private askSession(name: string, args: unknown): Promise<ToolOutcome> {
    const callId = `c${++this.seq}-${name}`
    return new Promise<ToolOutcome>((resolve) => {
      const timer = setTimeout(() => {
        this.pending.delete(callId)
        resolve({ ok: false, error: `the session did not answer "${name}" in time` })
      }, this.o.timeoutMs ?? DEFAULT_TIMEOUT_MS)

      this.pending.set(callId, (r) => {
        clearTimeout(timer)
        resolve(r)
      })
      this.o.emit({ t: 'tool.call', callId, name, args })
    })
  }
}
